/**
 * Mobile-aware token storage.
 * On Capacitor (native shell): persists tokens to secure storage so requests can
 *   attach `Authorization: Bearer <token>` headers (cookies are unreliable in
 *   WebViews that block third-party cookies).
 * On Web: no-op — the browser handles auth via httpOnly cookies set by the backend.
 *
 * The check uses a `window.Capacitor` global which exists only when running
 * inside a Capacitor-bundled native app.
 */

export const isNativeApp = () => {
  if (typeof window === 'undefined') return false;
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
};

const ACCESS_KEY = 'bsc_access_token';
const REFRESH_KEY = 'bsc_refresh_token';

// Lazy import so web bundles don't pull in Capacitor preferences code.
const getPreferences = async () => {
  if (!isNativeApp()) return null;
  try {
    const mod = await import('@capacitor/preferences');
    return mod.Preferences;
  } catch {
    return null;
  }
};

export const saveTokens = async ({ access_token, refresh_token }) => {
  if (!isNativeApp()) return;
  const Preferences = await getPreferences();
  if (!Preferences) return;
  if (access_token) await Preferences.set({ key: ACCESS_KEY, value: access_token });
  if (refresh_token) await Preferences.set({ key: REFRESH_KEY, value: refresh_token });
};

export const getAccessToken = async () => {
  if (!isNativeApp()) return null;
  const Preferences = await getPreferences();
  if (!Preferences) return null;
  const { value } = await Preferences.get({ key: ACCESS_KEY });
  return value || null;
};

export const getRefreshToken = async () => {
  if (!isNativeApp()) return null;
  const Preferences = await getPreferences();
  if (!Preferences) return null;
  const { value } = await Preferences.get({ key: REFRESH_KEY });
  return value || null;
};

export const clearTokens = async () => {
  if (!isNativeApp()) return;
  const Preferences = await getPreferences();
  if (!Preferences) return;
  await Preferences.remove({ key: ACCESS_KEY });
  await Preferences.remove({ key: REFRESH_KEY });
};
